import { useState } from "react";
import { fmtDate } from "@/lib/api";
import { History, ChevronDown, Flag, MessageSquareText, StickyNote, BellRing } from "lucide-react";

const KINDS = {
  etape: { label: "Étapes", icon: Flag, cls: "bg-brand-100 text-brand-700" },
  commentaire: { label: "Commentaires", icon: MessageSquareText, cls: "bg-sky-100 text-sky-700" },
  note: { label: "Notes", icon: StickyNote, cls: "bg-amber-100 text-amber-700" },
  relance: { label: "Relances", icon: BellRing, cls: "bg-violet-100 text-violet-700" },
};

function Event({ e, multi, idx }) {
  const k = KINDS[e.kind];
  const Icon = k.icon;
  const done = e.statut && e.statut !== "à faire";
  return (
    <li className="relative pl-9 pb-3 before:absolute before:left-3 before:top-7 before:bottom-0 before:w-px before:bg-slate-200 last:before:hidden" data-testid={`history-event-${idx}`}>
      <span className={`absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full ${k.cls}`}><Icon className="h-3.5 w-3.5" /></span>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
        <span className="font-mono">{fmtDate(e.date)}</span>
        {e.by && <span>· {e.by}</span>}
        {multi && <span className="rounded bg-slate-100 px-1.5 text-slate-600" data-testid={`history-prospect-${idx}`}>{e.prospect}</span>}
      </div>
      <p className="text-sm text-slate-700">
        {e.kind === "etape" && <>Étape : <b>{e.stage}</b></>}
        {e.kind === "commentaire" && <><span className="text-slate-400">[{e.stage}]</span> {e.text}</>}
        {e.kind === "note" && e.text}
        {e.kind === "relance" && <>
          Relance <b>{e.type}</b>{e.date_prevue && <> prévue le {fmtDate(e.date_prevue)}</>}
          <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${done ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>{done ? "faite" : "à faire"}</span>
          {e.text && <span className="block text-xs text-slate-500">{e.text}</span>}
        </>}
      </p>
      {e.kind === "etape" && e.text && <p className="text-xs text-slate-500">{e.text}</p>}
    </li>
  );
}

export default function ProspectHistory({ events }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(Object.keys(KINDS));
  if (!events?.length) return null;
  const multi = new Set(events.map((e) => e.prospect_numero)).size > 1;
  const shown = events.filter((e) => filters.includes(e.kind));
  const toggle = (k) => setFilters((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));
  return (
    <div className="mt-3 rounded-xl border bg-white" data-testid="prospect-history">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-3 text-sm font-head font-semibold text-slate-700" data-testid="prospect-history-toggle">
        <History className="h-4 w-4 text-brand-600" />Historique du prospect d'origine
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-500">{events.length}</span>
        <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t px-4 py-3">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {Object.entries(KINDS).map(([k, v]) => (
              <button key={k} type="button" onClick={() => toggle(k)} data-testid={`history-filter-${k}`}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${filters.includes(k) ? "border-brand-600 bg-brand-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                {v.label} ({events.filter((e) => e.kind === k).length})
              </button>
            ))}
          </div>
          {shown.length === 0 ? <p className="text-xs text-slate-400">Aucun élément pour ces filtres</p> : (
            <ul className="max-h-80 overflow-y-auto pr-1">
              {shown.map((e, i) => <Event key={i} e={e} multi={multi} idx={i} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
